import { loadStripe, type Stripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { useTranslation } from "react-i18next";

import { billingApi } from "@/api";
import { publicConfig } from "@/config/public";
import { Radius, Spacing, type ThemeColors } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import i18n from "@/i18n";

export type StripeCardFormProps = {
  /** Called after the new card is confirmed and set as default. */
  onDone: () => void;
  onCancel: () => void;
};

/** Dark palette background (constants/theme.ts) — what we match on to pick
 *  Stripe's night theme, since Elements has no notion of our theme. */
const DARK_BACKGROUND = "#0B1622";

/** Load Stripe.js once per publishable key. */
let stripePromise: Promise<Stripe | null> | null = null;
function getStripe(): Promise<Stripe | null> {
  const key = publicConfig.stripe.publishableKey;
  if (!key) return Promise.resolve(null);
  if (!stripePromise) stripePromise = loadStripe(key);
  return stripePromise;
}

/**
 * Web-only card capture using Stripe.js Elements. Card data is entered in
 * Stripe's own iframe (PaymentElement) and never touches our code — we only ever
 * see the resulting payment_method id, which we hand to the backend to set as
 * the default. Native platforms get the stub in stripe-card-form.tsx.
 */
export function StripeCardForm({ onDone, onCancel }: StripeCardFormProps) {
  useTranslation();
  const c = useTheme();
  const styles = useMemo(() => createStyles(c), [c]);
  const isDark = c.background === DARK_BACKGROUND;
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const { clientSecret: secret } = await billingApi.setupIntent();
        if (active) setClientSecret(secret || null);
      } catch {
        if (active) setError(i18n.t("billing.card.startError"));
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  if (!publicConfig.stripe.publishableKey) {
    return (
      <Text style={styles.error}>{i18n.t("billing.card.notConfigured")}</Text>
    );
  }

  if (error) return <Text style={styles.error}>{error}</Text>;

  if (!clientSecret) {
    return (
      <View style={styles.loading}>
        <ActivityIndicator color={c.tint} />
      </View>
    );
  }

  return (
    <Elements
      stripe={getStripe()}
      options={{
        clientSecret,
        appearance: {
          theme: isDark ? "night" : "stripe",
          // Match the subscribe page: drop the night theme's raised shadows,
          // which read as halos on our flat dark surfaces. `:focus` is left
          // alone and is more specific, so the focus ring survives.
          ...(isDark
            ? {
                rules: {
                  ".Input": { boxShadow: "none" },
                  ".Tab": { boxShadow: "none" },
                  ".Tab--selected": { boxShadow: "none" },
                  ".Block": { boxShadow: "none" },
                },
              }
            : {}),
        },
      }}
    >
      <CardFields
        styles={styles}
        colors={c}
        onDone={onDone}
        onCancel={onCancel}
      />
    </Elements>
  );
}

function CardFields({
  styles,
  colors,
  onDone,
  onCancel,
}: {
  styles: ReturnType<typeof createStyles>;
  colors: ThemeColors;
  onDone: () => void;
  onCancel: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  const submit = async () => {
    if (!stripe || !elements || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
      });
      if (result.error) {
        setError(result.error.message ?? i18n.t("billing.card.setupFailed"));
        return;
      }
      const pm = result.setupIntent?.payment_method;
      const pmId = typeof pm === "string" ? pm : pm?.id;
      if (!pmId) {
        setError(i18n.t("billing.card.setupIncomplete"));
        return;
      }
      await billingApi.setPaymentMethod(pmId);
      onDone();
    } catch {
      if (mounted.current) {
        setError(i18n.t("billing.card.genericError"));
      }
    } finally {
      if (mounted.current) setSubmitting(false);
    }
  };

  return (
    <View style={styles.form}>
      <PaymentElement />
      {error ? <Text style={styles.error}>{error}</Text> : null}
      <View style={styles.actions}>
        <Pressable
          accessibilityRole="button"
          disabled={submitting}
          onPress={onCancel}
          style={({ pressed }) => [
            styles.secondaryBtn,
            pressed && styles.pressed,
          ]}
        >
          <Text style={styles.secondaryBtnText}>{i18n.t("common.cancel")}</Text>
        </Pressable>
        <Pressable
          accessibilityRole="button"
          disabled={submitting || !stripe}
          onPress={submit}
          style={({ pressed }) => [
            styles.primaryBtn,
            (submitting || !stripe) && styles.disabled,
            pressed && styles.pressed,
          ]}
        >
          {submitting ? (
            <ActivityIndicator size="small" color={colors.onTint} />
          ) : (
            <Text style={styles.primaryBtnText}>
              {i18n.t("billing.card.save")}
            </Text>
          )}
        </Pressable>
      </View>
    </View>
  );
}

function createStyles(c: ThemeColors) {
  return StyleSheet.create({
    form: { gap: Spacing.md },
    loading: { paddingVertical: Spacing.lg, alignItems: "center" },
    actions: { flexDirection: "row", gap: Spacing.sm, marginTop: Spacing.xs },
    primaryBtn: {
      flex: 1,
      minHeight: 44,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: Radius.sm,
      backgroundColor: c.tint,
    },
    primaryBtnText: {
      color: c.onTint,
      fontSize: 15,
      fontWeight: "800",
    },
    secondaryBtn: {
      flex: 1,
      minHeight: 44,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: Radius.sm,
      borderWidth: 1,
      borderColor: c.border,
    },
    secondaryBtnText: { color: c.text, fontSize: 15, fontWeight: "700" },
    error: { color: c.danger, fontSize: 14, marginTop: Spacing.xs },
    disabled: { opacity: 0.6 },
    pressed: { opacity: 0.8 },
  });
}
